import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/authStore';

export const useProjectSwitcher = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // Get user-specific recent projects
  const getRecentProjects = useCallback(() => {
    if (!user?.id) return [];
    
    try {
      const storageKey = `projectSwitcher_${user.id}`;
      const data = localStorage.getItem(storageKey);
      const userData = data ? JSON.parse(data) : { recent: [], favorites: [] };
      return userData.recent || [];
    } catch (error) {
      console.error('Error getting recent projects:', error);
      return [];
    }
  }, [user?.id]);
  
  // Add project to recent list
  const addToRecentProjects = useCallback((projectId) => {
    if (!user?.id || !projectId) return;
    
    try {
      const storageKey = `projectSwitcher_${user.id}`;
      const data = localStorage.getItem(storageKey);
      const userData = data ? JSON.parse(data) : { recent: [], favorites: [] };
      
      // Remove if already exists and add to front
      const recent = userData.recent.filter(id => id !== projectId);
      recent.unshift(projectId);
      userData.recent = recent.slice(0, 5); // Keep only 5 most recent
      
      localStorage.setItem(storageKey, JSON.stringify(userData));
    } catch (error) {
      console.error('Error adding to recent projects:', error);
    }
  }, [user?.id]);
  
  // Quick switch to recent project by index (1-5)
  const switchToRecentProject = useCallback((index) => {
    const recentProjects = getRecentProjects();
    const projectId = recentProjects[index - 1];
    
    if (projectId) {
      navigate(`/projects/${projectId}`);
    }
  }, [getRecentProjects, navigate]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check for Cmd/Ctrl + number (1-5) for quick project switching
      if ((event.metaKey || event.ctrlKey) && event.key >= '1' && event.key <= '5') {
        event.preventDefault();
        const index = parseInt(event.key);
        switchToRecentProject(index);
      }
      
      // Cmd/Ctrl + P to open project switcher
      if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
        event.preventDefault();
        // Trigger custom event to open project switcher
        window.dispatchEvent(new CustomEvent('openProjectSwitcher'));
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [switchToRecentProject]);
  
  return {
    getRecentProjects,
    addToRecentProjects,
    switchToRecentProject
  };
};

export default useProjectSwitcher;